import CustomSelect from "@/components/shared/CustomSelect/CustomSelect";
import { MultiComboBox } from "@/components/shared/MultiComboBox/MultiComboBox";
import { Button } from "@/components/ui/button";
import { SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useSoldProductFilterOptions } from "@/hooks/useSoldProductFilterOptions";
import useCurrencyStore from "@/store/useCurrencyStore";
import useModalStore from "@/store/useModalStore";
import { fetchReportTemplateOptions } from "@/utils/checkoutTemplates";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

const FilterSchema = z.object({
  role: z.enum(["producer", "affiliate", "coproducer"]),
  products: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  offers: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  checkouts: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  affiliates: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  coproducers: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
  templates: z
    .array(z.object({ value: z.string(), label: z.string() }))
    .optional(),
});

export type FilterAnalyticsFormData = z.infer<typeof FilterSchema>;

type FilterValuesInput = {
  role?: string;
  products?: { value?: string; label?: string }[];
  offers?: { value?: string; label?: string }[];
  checkouts?: { value?: string; label?: string }[];
  affiliates?: { value?: string; label?: string }[];
  coproducers?: { value?: string; label?: string }[];
  templates?: { value?: string; label?: string }[];
};

export default function FilterAnalytics({
  onFilter,
  filterValues,
}: {
  filterValues: FilterValuesInput;
  onFilter: (data: FilterAnalyticsFormData) => void;
}) {
  const { t } = useTranslation(["sales", "reports", "common"]);
  const { closeModal } = useModalStore();
  const { currency } = useCurrencyStore();

  const {
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    clearErrors,
    reset,
    getValues,
  } = useForm<FilterAnalyticsFormData>({
    shouldUnregister: false,
    resolver: zodResolver(FilterSchema),
    defaultValues: {
      role:
        (filterValues?.role as FilterAnalyticsFormData["role"]) || "producer",
      products: filterValues?.products || [],
      offers: filterValues?.offers || [],
      checkouts: filterValues?.checkouts || [],
      affiliates: filterValues?.affiliates || [],
      coproducers: filterValues?.coproducers || [],
      templates: filterValues?.templates || [],
    },
  });

  useEffect(() => {
    reset({
      role:
        (filterValues?.role as FilterAnalyticsFormData["role"]) || "producer",
      products: filterValues?.products || [],
      offers: filterValues?.offers || [],
      checkouts: filterValues?.checkouts || [],
      affiliates: filterValues?.affiliates || [],
      coproducers: filterValues?.coproducers || [],
      templates: filterValues?.templates || [],
    });
  }, [filterValues, reset]);

  const {
    productIds,
    productIdsKey,
    offerIds,
    offerIdsKey,
    fetchProductOptions,
    fetchOfferOptions,
    fetchCheckoutsOptions,
    fetchAffiliatesOptions,
    fetchCoProducersOptions,
    handleProductsChange,
    handleOffersChange,
    queryKeys,
  } = useSoldProductFilterOptions({
    watch,
    setValue,
    getValues,
    currency,
    scope: "analytics",
    enableCheckouts: true,
    enableAffiliates: true,
    enableCoproducers: true,
  });

  const onSubmit = (data: FilterAnalyticsFormData) => {
    onFilter(data);
    closeModal("filter-analytics");
  };

  return (
    <section className="h-full flex relative flex-col justify-between">
      <SheetHeader className="md:p-6 p-4">
        <SheetTitle className="flex gap-2">
          {t("reports:filter_analytics")}
        </SheetTitle>
      </SheetHeader>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between h-full"
      >
        <section className="flex relative flex-col gap-4 px-4 md:px-6 mb-24">
          <CustomSelect
            label={t("reports:filter_view_as")}
            placeholder={t("reports:filter_view_as")}
            value={watch("role")}
            onValueChange={(value) =>
              setValue("role", value as FilterAnalyticsFormData["role"])
            }
            options={[
              { value: "producer", label: t("reports:role_producer") },
              { value: "affiliate", label: t("reports:role_affiliate") },
              { value: "coproducer", label: t("reports:role_coproducer") },
            ]}
          />

          <MultiComboBox
            label={t("sales:products")}
            placeholder={t("common:products_placeholder")}
            fetchOptions={fetchProductOptions}
            values={watch("products")}
            onChange={(value) => {
              handleProductsChange(value);
              clearErrors("products");
            }}
            helperText={errors.products?.message}
            queryKey="sold-products-analytics"
            returnFullObject
            cypressName="multicombobox-products-analytics"
            selectAllOptions
            newParams={[currency]}
            isEditing
          />

          <MultiComboBox
            key={`offers-${productIdsKey}`}
            label={t("sales:offer")}
            placeholder={t("sales:offer_placeholder")}
            fetchOptions={fetchOfferOptions}
            values={watch("offers")}
            onChange={(values) => {
              handleOffersChange(values);
              clearErrors("offers");
            }}
            helperText={errors.offers?.message}
            queryKey={queryKeys.offers}
            returnFullObject
            cypressName="multicombobox-offers-analytics"
            selectAllOptions
            newParams={productIds}
            disabled={productIds.length === 0}
            isEditing
          />

          <MultiComboBox
            key={`checkouts-${productIdsKey}-${offerIdsKey}`}
            label={t("common:select_checkout")}
            placeholder={t("common:select_checkout")}
            fetchOptions={fetchCheckoutsOptions}
            values={watch("checkouts")}
            onChange={(values) => {
              setValue("checkouts", values);
              clearErrors("checkouts");
            }}
            helperText={errors.checkouts?.message}
            queryKey={queryKeys.checkouts}
            returnFullObject
            cypressName="multicombobox-checkouts-analytics"
            selectAllOptions
            newParams={[...productIds, ...offerIds]}
            disabled={productIds.length === 0 || offerIds.length === 0}
            isEditing
          />

          <MultiComboBox
            key={`affiliates-${productIdsKey}`}
            label={t("sales:affiliate")}
            placeholder={t("reports:filter_affiliates")}
            fetchOptions={fetchAffiliatesOptions}
            values={watch("affiliates")}
            onChange={(values) => {
              setValue("affiliates", values);
              clearErrors("affiliates");
            }}
            helperText={errors.affiliates?.message}
            queryKey={queryKeys.affiliates}
            returnFullObject
            cypressName="multicombobox-affiliates-analytics"
            selectAllOptions
            newParams={productIds}
            isEditing
          />

          <MultiComboBox
            key={`coproducers-${productIdsKey}`}
            label={t("sales:co_producer")}
            placeholder={t("sales:co_producer")}
            fetchOptions={fetchCoProducersOptions}
            values={watch("coproducers")}
            onChange={(values) => {
              setValue("coproducers", values);
              clearErrors("coproducers");
            }}
            helperText={errors.coproducers?.message}
            queryKey={queryKeys.coproducers}
            returnFullObject
            cypressName="multicombobox-coproducers-analytics"
            selectAllOptions
            newParams={productIds}
            isEditing
          />

          <MultiComboBox
            label={t("reports:template")}
            placeholder={t("reports:select_template")}
            fetchOptions={fetchReportTemplateOptions}
            values={watch("templates")}
            onChange={(values) => {
              setValue("templates", values);
              clearErrors("templates");
            }}
            helperText={errors.templates?.message}
            queryKey="report-templates-analytics"
            returnFullObject
            cypressName="multicombobox-templates-analytics"
            selectAllOptions
            isEditing
          />
        </section>

        <SheetFooter className="flex flex-row gap-4 p-4 md:p-6">
          <Button
            type="button"
            variant="secondary"
            className="w-full"
            onClick={() => closeModal("filter-analytics")}
          >
            {t("sales:cancel")}
          </Button>
          <Button type="submit" className="w-full">
            {t("sales:apply_filters")}
          </Button>
        </SheetFooter>
      </form>
    </section>
  );
}
